import { memo, type ReactNode } from 'react';
import * as Tooltip from '~/components/ui/Tooltip';
import { classNames } from '~/utils/classNames';

interface SectionContainerProps {
  title: ReactNode;
  children: ReactNode;
  className?: string;
}

export const SectionContainer = memo(({ title, children, className }: SectionContainerProps) => {
  return (
    <>
      <div className="bg-bolt-ds-surfaceOne sticky top-0 z-layer-3 pt-12 mb-4">
        <div className="min-h-9">
          {typeof title === 'string' ? <h2 className="text-lg font-semibold truncate">{title}</h2> : title}
        </div>
      </div>

      <div className={classNames('flex flex-col gap-4', className)}>{children}</div>
    </>
  );
});

interface SectionRowProps {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  innerClassName?: string;
  descriptionClassName?: string;
  titleClassName?: string;
  layout?: 'horizontal' | 'vertical';
  tooltip?: ReactNode;
}

export const SectionRow = memo((props: SectionRowProps) => {
  const {
    title,
    description,
    action,
    layout = 'horizontal',
    className,
    innerClassName,
    descriptionClassName,
    titleClassName,
    tooltip,
  } = props;
  return (
    <div className={className}>
      <Tooltip.Root>
        <div
          className={classNames(
            'flex text-sm w-full min-h-[35px]',
            {
              'flex-row justify-between items-center gap-2 flex-wrap': layout === 'horizontal',
              'flex-col items-start': layout !== 'horizontal',
            },
            innerClassName,
          )}
        >
          {title && (
            <span
              className={classNames(
                'text-bolt-elements-textPrimary',
                layout === 'vertical' ? 'mb-2' : undefined,
                titleClassName,
              )}
            >
              {title}
            </span>
          )}
          {layout === 'vertical' && description && (
            <div className={classNames('text-sm text-bolt-elements-textSecondary w-full mb-2.5', descriptionClassName)}>
              {description}
            </div>
          )}
          {tooltip ? (
            <Tooltip.Trigger asChild>
              <span>{action}</span>
            </Tooltip.Trigger>
          ) : (
            action
          )}
        </div>
        {layout === 'horizontal' && description && (
          <div className={classNames('text-sm text-bolt-elements-textSecondary w-full mt-1.5', descriptionClassName)}>
            {description}
          </div>
        )}
        {tooltip && (
          <Tooltip.Portal>
            <Tooltip.Content side="top" align="center">
              {tooltip}
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>
    </div>
  );
});
